import { Box, Stack, Typography } from "@mui/material";
import "chart.js/auto";
import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCase } from "../../context/CaseContext";
import { useTheme } from "../../context/ThemeContext";
import { getSettledSummary } from "../../shared/CaseModelUtility";
import { adjustedOutcomes, ShownOutcome } from "../../shared/adjustedView";
import { OutcomesForParty } from "../../shared/Analytics";
import { groupOfOutcome } from "./CaseOutcomeUtility";
import { useOutcomeDialog } from "./OutcomeDialog";
import { useLitigation } from "./ShownResults";
import CenteredMessageBox from "../general/CenteredMessageBox";
import { Tabs } from "../general/Tabs";
import { TonalBadge } from "../general/TonalBadge";
import { byName, clientsFirst, sort } from "./AnalyticsUtility";
import { LawFirmRiskAnalysis } from "./LawFirmRiskAnalysis";
import { OutcomeRiskAnalysis } from "./OutcomeRiskAnalysis";
import { SettledEmptyState } from "./settlement/SettledEmptyState";
import { judgmentOf } from "../../shared/judgment";
import { JudgedState } from "../timeline/JudgedState";
import { GuideHeader } from "../guide/GuideHeader";

export const RiskAnalytics = () => {
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, { keyPrefix: "case" });
  const caseContext = useCase();
  const c = caseContext.getCase();
  const parties = caseContext.getPartyDescriptors();
  const outcomes = caseContext.getOutcomes();
  // the other view of each party's outcomes: as litigated in the
  // settlement-adjusted view, with settlements in the litigation view
  const litigation = useLitigation();
  const adjustedView = litigation !== caseContext;
  const adjusted = c.settlementAdjusted;
  const otherOf = (partyId: string) => {
    const litigated = litigation
      .getOutcomes()
      .find((o) => o.partyId === partyId);
    if (adjustedView || !litigated) return litigated;
    const party =
      adjusted?.status === "done"
        ? adjusted.parties.find((p) => p.partyId === partyId)
        : undefined;
    return party && adjustedOutcomes(litigated, party);
  };
  const sortedParties: string[] = useMemo(
    () => sort(parties, clientsFirst, byName),
    [parties],
  );

  // A fully settled case has no outcomes left to assess risk on: state the
  // agreed result, as Bargaining does, rather than an empty risk analysis.
  if (judgmentOf(c)) return <JudgedState />;
  const settled = getSettledSummary(c);
  if (settled) return <SettledEmptyState caseId={c.id} {...settled} />;

  const tabs: { label: ReactNode; tabComponent: ReactNode }[] =
    sortedParties.map((partyId) => {
      const party = parties[partyId];
      const name = party?.name || partyId;
      const outcome = outcomes.find((o) => o.partyId === partyId);
      return {
        label: (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <span>{name}</span>
            {party?.isClient && (
              <TonalBadge tone={theme.palette.primary}>
                {t("parties.clientBadge")}
              </TonalBadge>
            )}
          </Stack>
        ),
        tabComponent: outcome ? (
          <Stack spacing={2.25}>
            <PartyRisk
              outcome={outcome}
              partyName={name}
              litigated={adjustedView ? otherOf(partyId) : undefined}
              other={otherOf(partyId)}
            />
            {/* what the firm earns shows only where its costs are known: for
                its own clients, once it is known how its fees divide */}
            {outcome.average.lawFirm.costs === "known" && (
              <LawFirmRiskAnalysis outcomes={outcome.outcomes} />
            )}
          </Stack>
        ) : (
          <CenteredMessageBox
            message={t("analytics.risk.noOutcomes", { name })}
          />
        ),
      };
    });

  return (
    <Stack spacing={1.5} sx={{ userSelect: "none" }}>
      {/* the header, and at its end which results the page shows */}
      <GuideHeader>
        <Box>
          <Typography
            variant="h5"
            component="h1"
            sx={{ fontWeight: 700, letterSpacing: "-.01em" }}
          >
            {t("analytics.risk.title")}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 0.5, maxWidth: "76ch" }}
          >
            {t("analytics.risk.intro")}
          </Typography>
        </Box>
      </GuideHeader>
      <Box>
        <Tabs tabs={tabs} />
      </Box>
    </Stack>
  );
};

/**
 * The risk of one party, whose steps open the details of their outcomes in
 * the dialog the outcomes page has.
 */
const PartyRisk = ({
  outcome,
  partyName,
  litigated,
  other,
}: {
  outcome: OutcomesForParty<ShownOutcome>;
  partyName: string;
  litigated?: OutcomesForParty<ShownOutcome>;
  other?: OutcomesForParty<ShownOutcome>;
}) => {
  const caseContext = useCase();
  const outcomeDialog = useOutcomeDialog(outcome, partyName);
  return (
    <>
      <OutcomeRiskAnalysis
        outcome={outcome}
        parties={caseContext.getPartyDescriptors()}
        issues={caseContext.getIssueDescriptors()}
        litigated={litigated}
        other={other}
        onOpen={(opened) =>
          outcomeDialog.open(
            groupOfOutcome(opened, outcome.outcomes.indexOf(opened)),
          )
        }
      />
      {outcomeDialog.dialog}
    </>
  );
};
